import type { ProductImageEntry } from "./types";

const communication = (
  id: string,
  moduleSv: string,
  moduleEn: string,
  route: string,
  showsSv: string,
  showsEn: string,
  valueSv: string,
  valueEn: string,
): ProductImageEntry => ({
  id: `communications.${id}`,
  module: moduleSv,
  area: "operations",
  opens: route,
  setup: [
    "Öppna kommunikationsflödet i den syntetiska S5-organisationen",
    "Välj en befintlig kö och granska ett organisationstillhörigt ärende",
  ],
  scenario: "S5",
  visual: { variant: 0 },
  text: {
    sv: {
      alt: moduleSv,
      shows: showsSv,
      proves: valueSv,
    },
    en: {
      alt: moduleEn,
      shows: showsEn,
      proves: valueEn,
    },
  },
  use: ["pages/Plattform.tsx"],
});

const settings = (
  id: string,
  moduleSv: string,
  moduleEn: string,
  route: string,
  showsSv: string,
  showsEn: string,
  valueSv: string,
  valueEn: string,
): ProductImageEntry => ({
  id,
  module: moduleSv,
  area: id.startsWith("standing-data.") ? "standing-data" : "operations",
  opens: route,
  setup: [
    "Öppna Inställningar i den syntetiska S5-organisationen",
    "Granska den valda sektionen utan att ändra värden",
  ],
  scenario: "S5",
  visual: { variant: 0 },
  text: {
    sv: { alt: moduleSv, shows: showsSv, proves: valueSv },
    en: { alt: moduleEn, shows: showsEn, proves: valueEn },
  },
  use: ["pages/Plattform.tsx"],
});

const helpIntent = (
  id: string,
  moduleSv: string,
  moduleEn: string,
  route: string,
  showsSv: string,
  showsEn: string,
  valueSv: string,
  valueEn: string,
): ProductImageEntry => ({
  id: `help.center.${id}`,
  module: moduleSv,
  area: "operations",
  opens: route,
  setup: [
    "Öppna den inloggade plattformens Hjälpcenter",
    "Välj arbetsflödet och följ stegen och de tillhörande guiderna",
  ],
  scenario: "S5",
  visual: { variant: 0 },
  text: {
    sv: { alt: moduleSv, shows: showsSv, proves: valueSv },
    en: { alt: moduleEn, shows: showsEn, proves: valueEn },
  },
  use: ["pages/Plattform.tsx"],
});

export const GENERAL_DETAIL_CATALOGUE: readonly ProductImageEntry[] = [
  communication(
    "help",
    "Hjälp och drift",
    "Help and operations",
    "Kommunikation → Hjälp och drift",
    "Organisationens befintliga driftärenden och den valda ärendedetaljen.",
    "The organisation's existing operational requests and the selected request detail.",
    "Planeraren kan granska inkomna driftärenden och deras status i samma arbetsflöde.",
    "Planners can review incoming operational requests and their status in one workflow.",
  ),
  communication(
    "messages",
    "Besök och meddelanden",
    "Visits and messages",
    "Kommunikation → Besök och meddelanden",
    "Befintliga meddelandetrådar kopplade till besök, med den valda trådens innehåll.",
    "Existing visit-related message threads with the selected thread's content.",
    "Kontoret får en samlad vy över besöksmeddelanden och deras sammanhang.",
    "The office gets one view of visit messages and their context.",
  ),
  communication(
    "deviations",
    "Avvikelser",
    "Deviations",
    "Kommunikation → Avvikelser",
    "Organisationens befintliga avvikelser och den valda avvikelsens detaljer.",
    "The organisation's existing deviations and details for the selected deviation.",
    "Planeraren kan granska avvikelser innan en eventuell uppföljning.",
    "Planners can review deviations before any follow-up.",
  ),
  communication(
    "client",
    "Kund och anhörig",
    "Client and family",
    "Kommunikation → Kund och anhörig",
    "Organisationens befintliga önskemål från kunder eller anhöriga och den valda förfrågans detaljer.",
    "The organisation's existing requests from clients or relatives and details for the selected request.",
    "Önskemål visas i sitt sammanhang så att verksamheten kan bedöma dem.",
    "Requests appear in context so the organisation can assess them.",
  ),
  {
    id: "operations.notifications-inbox",
    module: "Aviseringar",
    area: "operations",
    opens: "Aviseringar → Alla",
    setup: [
      "Öppna aviseringar för den inloggade S5-organisationen",
      "Granska en befintlig syntetisk avisering och dess händelselogg",
    ],
    scenario: "S5",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Aviseringsinkorg med en vald avisering",
        shows:
          "Inkorgen visar befintliga aviseringar, prioritet och detaljer för den valda posten.",
        proves:
          "Aviseringens prioritet, källa och händelse visas tillsammans så att planeraren kan bedöma nästa steg.",
      },
      en: {
        alt: "Notification inbox with a selected notification",
        shows:
          "The inbox shows existing notifications, their priority and details for the selected item.",
        proves:
          "The notification's priority, source and event are shown together to help the planner assess the next step.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  helpIntent(
    "getting-started",
    "Kom igång i plattformen",
    "Getting started in the platform",
    "Hjälp → Kom igång",
    "Datagrundens verkliga status, nästa steg och guider för att förbereda planeringen.",
    "The live data-foundation status, next steps and guides for preparing planning.",
    "Hjälpen kopplar organisationens aktuella grundstatus till relevanta instruktioner.",
    "Help connects the organisation's current foundation status to relevant guidance.",
  ),
  helpIntent(
    "planning",
    "Planera ett schema",
    "Plan a schedule",
    "Hjälp → Planera",
    "Stegen för att skapa, analysera, finjustera och jämföra planeringsresultat.",
    "Steps for creating, analysing, tuning and comparing planning results.",
    "Planeringsflödet samlar åtgärder och guider i den ordning planeraren använder dem.",
    "The planning workflow brings actions and guides together in the order a planner uses them.",
  ),
  helpIntent(
    "operations",
    "Arbeta med ett publicerat schema",
    "Work with a published schedule",
    "Hjälp → Arbeta i drift",
    "Steg och guider för publicering, ändringar och uppföljning i driften.",
    "Steps and guides for publishing, changes and operational follow-up.",
    "Hjälpen visar arbetsflödet för ett publicerat schema utan att utföra åtgärder.",
    "Help shows the workflow for a published schedule without carrying out actions.",
  ),
  helpIntent(
    "follow-up",
    "Följ upp planeringen",
    "Follow up planning",
    "Hjälp → Följ upp",
    "Guider för att följa upp planeringsresultat och rapporter.",
    "Guides for following up planning results and reports.",
    "Planeraren hittar uppföljningsstöd samlat efter att schemat tagits fram.",
    "Planners can find follow-up guidance together after a schedule is prepared.",
  ),
  settings(
    "standing-data.organization-overview",
    "Organisation",
    "Organisation",
    "Inställningar → Organisation",
    "Organisationens verkliga namn, tidszon, kontaktuppgifter och anslutna organisationstjänster.",
    "The organisation's actual name, time zone, contact details and connected organisation services.",
    "Organisationsuppgifterna går att granska samlat innan en behörig användare väljer att redigera dem.",
    "Organisation details can be reviewed together before an authorised user chooses to edit them.",
  ),
  settings(
    "operations.employee-rules",
    "Medarbetarregler",
    "Employee rules",
    "Inställningar → Medarbetarregler",
    "Organisationens aktuella regler för personal, kostnad och arbetspass.",
    "The organisation's current rules for staff, costs and shifts.",
    "Reglerna visas i en skrivskyddad sektion och kan granskas utan att sparas om.",
    "The rules appear in a read-only section and can be reviewed without saving changes.",
  ),
  settings(
    "standing-data.care-catalogue",
    "Insatser och grupper",
    "Care interventions and groups",
    "Inställningar → Insatser",
    "Organisationens befintliga insatstyper och grupper.",
    "The organisation's existing care-intervention types and groups.",
    "Katalogen visar vilka insatser som utgör verksamhetens planeringsunderlag.",
    "The catalogue shows which interventions form the organisation's planning inputs.",
  ),
  settings(
    "standing-data.skills-and-tags",
    "Kompetenser och taggar",
    "Skills and tags",
    "Inställningar → Kompetenser och taggar",
    "Organisationens befintliga kompetenser och taggar.",
    "The organisation's existing skills and tags.",
    "Kompetenser och taggar visas som gemensamma värden för verksamhetens matchning.",
    "Skills and tags are shown as shared values for the organisation's matching rules.",
  ),
  settings(
    "operations.optimization-defaults",
    "Planeringsprofiler",
    "Planning profiles",
    "Inställningar → Planeringsprofiler",
    "Organisationens aktuella standardvärden för planering och optimering.",
    "The organisation's current planning and optimisation defaults.",
    "Standardvärdena kan granskas som organisationens utgångspunkt för nya planeringar.",
    "The defaults can be reviewed as the organisation's starting point for new plans.",
  ),
  settings(
    "operations.scheduler-appearance",
    "Kalenderutseende",
    "Calendar appearance",
    "Inställningar → Utseende",
    "De befintliga färgvalen för schemaläggarens kalender.",
    "The existing colour choices for the scheduler calendar.",
    "Vyn visar hur organisationens schemavisning är inställd utan att ändra färgerna.",
    "The view shows the organisation's schedule display settings without changing them.",
  ),
  {
    id: "operations.product-access",
    module: "Produkter",
    area: "operations",
    opens: "Konto → Produkter",
    setup: [
      "Öppna kontots produktflik med den syntetiska S5-organisationens behörigheter",
      "Granska tillgängliga produktkort och faktisk åtkomststatus",
    ],
    scenario: "S5",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Produktöversikt med faktiska åtkomststatusar",
        shows:
          "Produktöversikten visar produktkort och organisationens aktuella åtkomststatus.",
        proves:
          "Åtkomststatusen kommer från organisationens behörigheter och kan granskas utan att starta köp eller aktivering.",
      },
      en: {
        alt: "Product overview with actual access statuses",
        shows:
          "The product overview shows product cards and the organisation's current access status.",
        proves:
          "Access status comes from the organisation's entitlements and can be reviewed without starting a purchase or activation.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
];
